import ManageableTypesEnum from "@/consts/ManageableTypesEnum"
import WaitlistSettingsLayout from "@/components/layouts/WaitlistSettingsLayout"
import CustomFieldForm from "@/components/pages/manage/custom-fields/CustomFieldForm"
import Slide from "@/components/common/Slide"
import ManageListCard from "@/components/pages/manage/ManageListCard"
import { useMemo, useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  deleteCustomField,
  updateCustomField,
} from '@/api/nextme/custom-fields'
import { setAlert } from '@/store/slices/alertSlice'
import { UserRoleNames } from '@/consts/UserRolesEnum'
import { setIsCustomFieldFormOpen } from '@/store/slices/pages/manageSlice'
import useCustomFields from '@/hooks/api-hooks/useCustomFields'
import useQueues from '@/hooks/api-hooks/useQueues'
import { hasMinimumRole } from '@/utils/permissions'
import { useTypedSelector } from '@/store/index'
import { getSelectedObjectById, printAxiosErrors } from '@/utils/util-functions'
import { CustomField } from '@/api/interface/custom-field'
import SharedDetailStatus from '@/components/common/SharedDetailStatus'
import { setEditingCFId } from '@/store/slices/pages/settingsSlice'
import { ManageListProps } from '@/components/pages/manage/interface'
import { setIsUsingCustomQueueSelector } from '@/store/slices/pages/manageSlice'
import useIsUsingCustomQueueSelector from '@/hooks/useIsUsingCustomQueueSelector'
import useAddCFToSCI from '@/hooks/useAddCFToSCI'
import useDragReordering from '@/hooks/useDragReordering'
import { getMovedItemPosition } from '@/utils/drag-reorder-utils'
import { updateCustomFieldOrder } from '@/api/nextme/custom-fields'
import useSelectedLocation from "@/hooks/api-selected-hooks/useSelectedLocation"

const CF_TYPE_LABEL_MAP = {
  multiselect: 'Multiselect Dropdown',
  select: 'Dropdown',
  checkbox: 'Checkbox',
  radio: 'Radio Input',
  string: 'Short Text',
  text: 'Long Text',
  tel: 'Phone #',
  email: 'Email Address',
  number: 'Number',
  date: 'Date',
  time: 'Time Input',
  queueselect: 'Queue Assignment',
}

// const CF_ICON_MAP = (type) => {
//   switch (type) {
//     case "multiselect":
//     case "dropdown":
//     case "select":
//     case "radio":
//       return "dropdown"
//   }
//   return "freeform"
// }

export default function CustomFieldsPage() {
  const dispatch = useDispatch()
  const { isUsingCustomQueueSelector } = useIsUsingCustomQueueSelector(false)
  const { userRole, businessId } = useTypedSelector(
    (state) => state.selected
  )
  const { locationId } = useSelectedLocation()
  
  const {
    sciToUpdate,
    updateSci,
    mutateSelfCheckIns,
    resetPublicQueueSelector,
  } = useAddCFToSCI()
  const { editingCFId } = useTypedSelector((state) => state.pages.settings)

  const { hasOnlyOneLocation, isCustomFieldFormOpen, isUsingSharedSettings } =
    useTypedSelector((state) => state.pages.manage)

  const {
    customFields,
    isValidating: isCfValidating,
    mutateCustomFields,
  } = useCustomFields()

  const { queues, isValidating: isQueuesValidating, mutateQueues } = useQueues()
  const [hasSavedChanges, setHasSavedChanges] = useState(false)

  const sharedQueues = useMemo(
    () => queues.filter((x) => x.queueableId === businessId),
    [queues]
  )

  const locationQueues = useMemo(
    () => queues.filter((x) => x.queueableId === locationId),
    [queues, locationId]
  )

  const editingCustomField = useMemo<CustomField>(
    () => getSelectedObjectById(editingCFId, customFields),
    [editingCFId, customFields]
  )

  const canEditBusinessCF = hasMinimumRole(UserRoleNames.rm, userRole)

  const canEditLocationCF = hasMinimumRole(UserRoleNames.lm, userRole)

  const lastCfIndex = [...customFields]
    .sort((a, b) => a.index - b.index)
    .slice(-1)[0]?.index
  const newIndex = lastCfIndex ? lastCfIndex + 1 : 0

  //@ts-ignore - TDOD: fix during QA
  const customFieldsListData: ManageListProps['editableListData'] =
    useMemo(() => {
      let filteredCFs = customFields

      if (isUsingSharedSettings || hasOnlyOneLocation) {
        filteredCFs = customFields.filter(
          (item) => item.cfableType === 'business'
        )
      }

      return filteredCFs.map((x) => ({
        id: x.id,
        canDeactivate: false,
        canDelete:
          (isUsingSharedSettings &&
            canEditBusinessCF &&
            x.cfableType === 'business') || // shared settings is selected
          (hasOnlyOneLocation && canEditBusinessCF) ||
          (x.cfableType === 'location' && !isUsingSharedSettings),
        canEdit:
          (isUsingSharedSettings &&
            canEditBusinessCF &&
            x.cfableType === 'business') || // shared settings is selected
          (hasOnlyOneLocation && canEditBusinessCF) ||
          (x.cfableType === 'location' && !isUsingSharedSettings),
        disableEdit: false,
        deactivated: false,
        name: x.name,
        type: x.type,
        details: `${CF_TYPE_LABEL_MAP[x.type]}`,
        iconSrc: `/icons/custom-field.svg`,
        detailStatusComponent: !hasOnlyOneLocation &&
          x.cfableType === 'business' && <SharedDetailStatus />,
        readOnly:
          (x.cfableType === 'business' && !canEditBusinessCF) ||
          (x.cfableType === 'location' && !canEditLocationCF) ||
          (x.cfableType === 'business' &&
            !isUsingSharedSettings &&
            !hasOnlyOneLocation),
      }))
    }, [customFields, isUsingSharedSettings])

  const { setIsDraggable, setApiReorderCallback, setDragItems } =
    useDragReordering()

  const updateCfPosition = async (
    movedItem: Partial<CustomField>,
    items: Array<Partial<CustomField>>
  ) => {
    try {
      const newPosition = getMovedItemPosition(movedItem, items)
      const initPosition = getMovedItemPosition(movedItem, customFieldsListData)
      if (newPosition !== initPosition) {
        const res = await updateCustomFieldOrder(movedItem, newPosition)
        await mutateCustomFields()
        dispatch(
          setAlert({
            isOpen: true,
            duration: 3000,
            type: 'success',
            title: 'Custom Fields updated.',
            message: 'Custom Fields order has been updated',
          })
        )
      }
    } catch (err) {
      console.error(err)
      dispatch(
        setAlert({
          isOpen: true,
          duration: 3000,
          title: 'There was an issue reordering Custom Fields',
          type: 'error',
          message: printAxiosErrors(err, true),
        })
      )
    }
  }

  useEffect(() => {
    setIsDraggable(true)
    setApiReorderCallback(updateCfPosition)
  }, [])

  const openForm = () => {
    editItem(null)
  }

  const closeForm = () => {
    dispatch(setIsCustomFieldFormOpen(false))
    dispatch(setEditingCFId(null))
  }

  const dispatchCustomQueueSelector = (isUsingCustomQueueSelector: boolean) => {
    dispatch(setIsUsingCustomQueueSelector(isUsingCustomQueueSelector))
  }

  useEffect(() => {
    if (hasSavedChanges) {
      setDragItems(customFieldsListData)
      setHasSavedChanges(false)
    }
  }, [hasSavedChanges, customFieldsListData])

  useEffect(() => {
    // reset drag items if the location picker changes
    setDragItems(customFieldsListData)
  }, [locationId, isUsingSharedSettings])

  const handleSave = async () => {
    // update all SCIs custom fields to include this custom field if set to all or self_check_in
    await mutateCustomFields()
    await mutateQueues()
    setHasSavedChanges(true)
    closeForm()
  }

  const deleteItem = async (id: CustomField['id']) => {
    const cf = getSelectedObjectById(id, customFields)
    try {
      await deleteCustomField(id)
      await handleSave()
      await mutateCustomFields()

      if (cf && cf.type === 'queueselect') {
        dispatchCustomQueueSelector(false)
        resetPublicQueueSelector(sciToUpdate)
      }

      dispatch(
        setAlert({
          isOpen: true,
          title: 'Field Deleted',
          type: 'success',
          message: 'Your custom field has been deleted.',
        })
      )
    } catch (err) {
      console.error(printAxiosErrors(err))

      dispatch(
        setAlert({
          isOpen: true,
          title: err.response?.data?.message,
          type: 'error',
          message: printAxiosErrors(err, true),
        })
      )
    }
  }

  const editItem = (id: CustomField['id'] | null) => {
    dispatch(setEditingCFId(id))
    dispatch(setIsCustomFieldFormOpen(true))
  }

  const deactivateItem = async (id: CustomField['id']) => {
    const cf = customFields.find((x) => x.id === id)

    if (!cf) return

    try {
      await updateCustomField({
        id: cf.id,
        type: cf.type,
        enabledAt: [],
      })

      await handleSave()

      if (cf.type === 'queueselect') {
        dispatchCustomQueueSelector(false)
        resetPublicQueueSelector(sciToUpdate)
      }

      dispatch(
        setAlert({
          isOpen: true,
          title: 'Field Deactivated',
          type: 'success',
          message: 'Your custom field has been deactivated.',
        })
      )
    } catch (err) {
      console.error(printAxiosErrors(err))

      dispatch(
        setAlert({
          isOpen: true,
          title: err.response?.data?.message,
          type: 'error',
          message: printAxiosErrors(err),
        })
      )
    }
  }

  const reactivateItem = async (cfId: string) => {
    try {
      const cf = getSelectedObjectById(cfId, customFields)

      await updateCustomField({
        id: cf.id,
        type: cf.type,
        enabledAt:
          cf.cfableType === 'business'
            ? sharedQueues.map((x) => x.id)
            : locationQueues.map((x) => x.id),
      })

      await handleSave()

      if (cf && cf.type === 'queueselect') {
        dispatchCustomQueueSelector(true)
      }

      dispatch(
        setAlert({
          isOpen: true,
          title: 'Field Reactivated',
          type: 'success',
          message: 'Your custom field has been reactivated.',
        })
      )
    } catch (err) {
      console.error(printAxiosErrors(err))

      dispatch(
        setAlert({
          isOpen: true,
          title: err.response?.data?.message,
          type: 'error',
          message: printAxiosErrors(err),
        })
      )
    }
  }

  const shouldLoadWholePage = useMemo(
    () => isCfValidating || isQueuesValidating,
    [isCfValidating, isQueuesValidating]
  )

  return (
    <WaitlistSettingsLayout
      title={ManageableTypesEnum.CUSTOM_FIELD}
      shouldLoadWholePage={shouldLoadWholePage}
    >
      <ManageListCard
        config={{
          title: 'Custom Fields',
          type: 'Custom Field',
          editPageSubtitle: 'Capture additional info about your guests.',
        }}
        actions={{
          editItem,
          deleteItem,
          deactivateItem,
          reactivateItem,
        }}
        editableListData={customFieldsListData}
        onCreateClick={openForm}
      />
      <Slide isOpen={isCustomFieldFormOpen} handleClose={closeForm}>
        <CustomFieldForm
          canEditBusinessCF={canEditBusinessCF}
          customField={editingCustomField}
          handleFormSuccess={handleSave}
          orderIndex={editingCustomField?.index || newIndex}
        />
      </Slide>
    </WaitlistSettingsLayout>
  )
}
